import {
  createContext,
  type ComponentType,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

type InfoContentComponent = ComponentType<object>

interface InfoPanelContextValue {
  isOpen: boolean
  activeContent: InfoContentComponent | null
  showInfoPanel: (content: InfoContentComponent) => void
  hideInfoPanel: () => void
  toggleInfoPanel: (content: InfoContentComponent) => void
}

const InfoPanelContext = createContext<InfoPanelContextValue | null>(null)

interface InfoPanelContextProviderProps {
  children?: ReactNode
}

export function InfoPanelContextProvider({
  children,
}: InfoPanelContextProviderProps) {
  const [activeContent, setActiveContent] =
    useState<InfoContentComponent | null>(null)

  const showInfoPanel = useCallback((content: InfoContentComponent) => {
    setActiveContent(() => content)
  }, [])

  const hideInfoPanel = useCallback(() => {
    setActiveContent(null)
  }, [])

  const toggleInfoPanel = useCallback((content: InfoContentComponent) => {
    setActiveContent((current: InfoContentComponent | null) =>
      current === content ? null : content,
    )
  }, [])

  const value = useMemo<InfoPanelContextValue>(
    () => ({
      isOpen: activeContent !== null,
      activeContent,
      showInfoPanel,
      hideInfoPanel,
      toggleInfoPanel,
    }),
    [activeContent, showInfoPanel, hideInfoPanel, toggleInfoPanel],
  )

  return <InfoPanelContext value={value}>{children}</InfoPanelContext>
}

export function useInfoPanelContext(): InfoPanelContextValue {
  const context = useContext(InfoPanelContext)
  if (!context) {
    throw new Error(
      'useInfoPanelContext must be used within an InfoPanelContextProvider',
    )
  }
  return context
}
